import { Component } from '@theme/component';

/**
 * Enhances the contact modal's form. The form itself submits natively — a fetch
 * POST to /contact is answered with Shopify's bot-verification page rather than
 * the form response, so a full page navigation is the only reliable path. The
 * hidden `return_to` field carries the visitor on to the thank-you page.
 *
 * That leaves this component two jobs, both around the navigation:
 *
 * 1. Block a second submit while the first is in flight.
 * 2. Reopen the popup when Shopify sends the visitor back here with a message.
 *    The dialog is closed after a page load, so the message would otherwise
 *    render inside a popup nobody can see.
 *
 * The `submit` event bubbles, so the component listens on itself rather than
 * needing an `on:submit` attribute on the Shopify-generated `<form>`.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} submitButton – The form's submit button.
 *
 * @extends Component<Refs>
 */
export class ContactFormComponent extends Component {
  requiredRefs = ['submitButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('submit', this.handleSubmit);

    if (this.querySelector('[data-form-message]')) this.#reopen();
  }

  /**
   * Marks the form as submitting. The browser has already started the
   * navigation by the time this runs, and the button carries no `name`, so
   * disabling it here cannot drop anything from the posted data.
   */
  handleSubmit = () => {
    const { submitButton } = this.refs;

    submitButton.setAttribute('disabled', '');
    submitButton.setAttribute('aria-busy', 'true');
  };

  /**
   * Reopens the popup over a server-rendered message. Waits for
   * `dialog-component` to be defined, since the two modules race on load.
   */
  async #reopen() {
    await customElements.whenDefined('dialog-component');

    const dialog = /** @type {{ showDialog?: () => void }} */ (this.closest('dialog-component'));

    dialog?.showDialog?.();
  }
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactFormComponent);
}
